import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip — AuthCallback handles exchange.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Dual-strategy auth: httpOnly cookie (set by server) + bearer token in
    // localStorage. The bearer is the fallback for cross-origin preview
    // environments where third-party cookies are blocked. Both tokens map to
    // the same server-side session row.
    if (data.session_token) localStorage.setItem("telesis_session_token", data.session_token);
    // Hit /auth/me so user carries the full shape (age_verified, membership_tier, votes_remaining…).
    await checkAuth();
    return data.user;
  }, [checkAuth]);

  const registerEmail = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.session_token) localStorage.setItem("telesis_session_token", data.session_token);
    await checkAuth();
    return data.user;
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout API call failed, clearing local state anyway", err);
    }
    localStorage.removeItem("telesis_session_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginEmail, registerEmail, logout, setUser, refreshUser: checkAuth }),
    [user, loading, loginEmail, registerEmail, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
